import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Anchor, ChevronDown, Loader2, RefreshCw, Ship } from 'lucide-react';

import http from '@/api/http';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import { VesselMarker } from './components/VesselMarker';
import { PortMarker } from './components/PortMarker';
import { VesselDetailsSidebar } from './components/VesselDetailsSidebar';
import { PortDetailsSidebar } from './components/PortDetailsSidebar';
import { ContainerDetailsModal } from './components/ContainerDetailsModal';
import {
  ActivePortData,
  ActiveVesselData,
  Booking,
  BookingBill,
  BookingContainer,
  MapContainerData,
  Movement,
  YML_Vessel_Schedule,
} from './types';

/** Zoom ușor + centrare pe coordonate (navă/port) când se selectează din listă. */
type MapFocusTarget = { lat: number; lng: number; zoom: number; token: number };

function isValidMapCoords(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  // 0,0 = fallback greșit (navă fără AIS) — nu centrăm acolo
  if (lat === 0 && lng === 0) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return true;
}

/** Componentă interioară MapContainer — are acces la instanța Leaflet. */
const MapFlyTo: React.FC<{ target: MapFocusTarget | null }> = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (!isValidMapCoords(target.lat, target.lng)) return;
    map.flyTo([target.lat, target.lng], target.zoom, { duration: 0.85 });
  }, [target, map]);
  return null;
};

export const TrackingMapPage: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const { direction } = useDirection();
  const isRo = currentLanguage === 'ro';
  // Import: navele care aduc marfa spre Constanța. Export: navele care pleacă
  // din Constanța (ROCND) spre POD. Filtrarea se face pe backend, prin `direction`.
  const isExport = direction === 'export';

  const [vesselData, setVesselData] = useState<ActiveVesselData[]>([]);
  const [portData, setPortData] = useState<ActivePortData[]>([]);
  const [selectedVessel, setSelectedVessel] = useState<ActiveVesselData | null>(null);
  const [selectedPort, setSelectedPort] = useState<ActivePortData | null>(null);
  const [selectedContainerData, setSelectedContainerData] = useState<MapContainerData | null>(null);
  const [mapFocus, setMapFocus] = useState<MapFocusTarget | null>(null);
  const [loading, setLoading] = useState(true);
  // Lista „În tranzit acum": pe desktop e deschisă (overlay), pe mobil pornește
  // compactată ca un dropdown — utilizatorul o extinde la nevoie.
  const [listExpanded, setListExpanded] = useState<boolean>(
    () => typeof window === 'undefined' || window.innerWidth > 900,
  );

  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { direction };
      const [vResponse, pResponse] = await Promise.all([
        http.get('/dashboard/tracking-vessels', { params }),
        http.get('/dashboard/tracking-ports', { params }),
      ]);

      const vMapped: ActiveVesselData[] = vResponse.data.data.map((item: any) => ({
        vessel: {
          id: item.vessel.id.toString(),
          vesselName: item.vessel.vesselName,
          voyageCode: item.vessel.voyageCode,
          currentLocation: {
            lat: item.vessel.lat,
            lng: item.vessel.lng,
            name: item.vessel.vesselName,
            heading: item.vessel.heading,
          },
          nextPort: item.vessel.nextPort || 'N/A',
          etaNextPort: item.vessel.etaNextPort ? new Date(item.vessel.etaNextPort) : null,
        },
        containers: item.containers.map((c: any) => ({
          container: {
            id: c.containerId.toString(),
            containerNumber: c.containerNumber,
            size: c.size,
            type: c.type,
            grossWeight: c.grossWeight,
            packageNumber: c.packageNumber,
            sealNumber: c.sealNumber,
          },
          movements: c.movements,
          booking: { bookingReference: c.bookingNumber, clientName: c.clientName },
          bill: { billNumber: c.billNumber, translatedCommodity: c.translatedCommodity, commodity: c.commodity },
        })),
      }));

      const pMapped: ActivePortData[] = pResponse.data.data.map((item: any) => ({
        port: {
          id: item.portId,
          name: item.name,
          coordinates: { lat: item.lat, lng: item.lng },
        },
        containers: item.containers.map((c: any) => ({
          container: {
            id: c.containerId.toString(),
            containerNumber: c.containerNumber,
            size: c.size,
            type: c.type,
            grossWeight: c.grossWeight,
            packageNumber: c.packageNumber,
            sealNumber: c.sealNumber,
          },
          movements: c.movements,
          booking: { bookingReference: c.bookingNumber, clientName: c.clientName },
          bill: { billNumber: c.billNumber, translatedCommodity: c.translatedCommodity, commodity: c.commodity },
        })),
      }));

      setVesselData(vMapped);
      setPortData(pMapped);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch tracking data', error);
    } finally {
      setLoading(false);
    }
  }, [direction]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const focusMapOn = useCallback((lat: number, lng: number, zoom = 7) => {
    if (!isValidMapCoords(lat, lng)) return;
    setMapFocus({ lat, lng, zoom, token: Date.now() });
  }, []);

  const selectVessel = useCallback(
    (fullData: ActiveVesselData, { toggleOff = false }: { toggleOff?: boolean } = {}) => {
      if (toggleOff) {
        setSelectedVessel(null);
        return;
      }
      setSelectedPort(null);
      setSelectedVessel(fullData);
      const { lat, lng } = fullData.vessel.currentLocation || { lat: NaN, lng: NaN };
      focusMapOn(lat, lng, 7);
    },
    [focusMapOn],
  );

  const selectPort = useCallback(
    (fullData: ActivePortData, { toggleOff = false }: { toggleOff?: boolean } = {}) => {
      if (toggleOff) {
        setSelectedPort(null);
        return;
      }
      setSelectedVessel(null);
      setSelectedPort(fullData);
      const { lat, lng } = fullData.port.coordinates || { lat: NaN, lng: NaN };
      focusMapOn(lat, lng, 8);
    },
    [focusMapOn],
  );

  const handleVesselClick = (vessel: YML_Vessel_Schedule) => {
    const fullData = vesselData.find((d) => d.vessel.id === vessel.id);
    if (fullData) selectVessel(fullData);
  };

  const handlePortClick = (portId: string) => {
    const fullData = portData.find((d) => d.port.id === portId);
    if (fullData) selectPort(fullData);
  };

  const handleContainerClick = (
    container: BookingContainer,
    booking: Booking,
    bill: BookingBill,
    movements: Movement[],
  ) => {
    setSelectedContainerData({ container, booking, bill, movements });
  };

  /** Porturi cu cel puțin un container (deja filtrate pe backend). */
  const sortedPorts = useMemo(
    () =>
      [...portData]
        .filter((p) => (p.containers?.length || 0) > 0)
        .sort((a, b) => (b.containers?.length || 0) - (a.containers?.length || 0)),
    [portData],
  );

  type TrackItem =
    | { kind: 'vessel'; count: number; data: ActiveVesselData }
    | { kind: 'port'; count: number; data: ActivePortData };

  /** Listă unificată: nave + porturi, sortate după nr. containere. */
  const trackItems = useMemo<TrackItem[]>(() => {
    const vessels: TrackItem[] = vesselData.map((data) => ({
      kind: 'vessel',
      count: data.containers?.length || 0,
      data,
    }));
    const ports: TrackItem[] = sortedPorts.map((data) => ({
      kind: 'port',
      count: data.containers?.length || 0,
      data,
    }));
    return [...vessels, ...ports].sort((a, b) => b.count - a.count);
  }, [vesselData, sortedPorts]);

  const totalContainers = useMemo(
    () => trackItems.reduce((sum, item) => sum + item.count, 0),
    [trackItems],
  );

  const trackCountLabel = useMemo(() => {
    const v = vesselData.length;
    const p = sortedPorts.length;
    if (isRo) {
      return `${v} ${v === 1 ? 'navă' : 'nave'} · ${p} ${p === 1 ? 'port' : 'porturi'} · ${totalContainers} ${
        totalContainers === 1 ? 'container' : 'containere'
      }`;
    }
    return `${v} ${v === 1 ? 'vessel' : 'vessels'} · ${p} ${p === 1 ? 'port' : 'ports'} · ${totalContainers} ${
      totalContainers === 1 ? 'container' : 'containers'
    }`;
  }, [vesselData.length, sortedPorts.length, totalContainers, isRo]);

  return (
    <div style={{ color: 'var(--text)' }}>
      {/* Header */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', margin: 0 }}>
            {t('map.title')}
          </h1>
          <div className="muted fs-13 mt-1">{t('map.subtitle')}</div>
        </div>
        <div className="row gap-2">
          <button className="btn btn-sm" onClick={fetchData} disabled={loading} type="button">
            {loading ? (
              <Loader2 size={13} className="ai-spin" />
            ) : (
              <RefreshCw size={13} />
            )}
            {t('map.refresh')}
          </button>
        </div>
      </div>

      <div className="map-layout">
        {/* În tranzit acum — overlay listă nave urmărite (desktop) / bloc sus (mobil) */}
        <div className={'map-overlay-list' + (listExpanded ? ' expanded' : ' collapsed')}>
          <button
            type="button"
            className="head"
            onClick={() => setListExpanded((v) => !v)}
            aria-expanded={listExpanded}
          >
            <div
              className="row gap-2"
              style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="fw-600 fs-13">{t('map.tracks')}</div>
                <div className="muted fs-11 mt-1">{trackCountLabel}</div>
              </div>
              <div className="row gap-2" style={{ flexShrink: 0 }}>
                <div className="badge badge-info">
                  <span
                    className="dot"
                    style={{ animation: 'pulseRing 2s ease-out infinite' }}
                  />
                  LIVE
                </div>
                <ChevronDown
                  size={16}
                  className="map-list-chevron"
                  style={{
                    transition: 'transform 0.2s',
                    transform: listExpanded ? 'rotate(180deg)' : 'none',
                    color: 'var(--text-muted)',
                  }}
                />
              </div>
            </div>
          </button>
          <div className="list" hidden={!listExpanded}>
            {trackItems.length === 0 ? (
              <div className="empty" style={{ padding: 24 }}>
                <div className="muted fs-12">{t('map.tracks.empty')}</div>
              </div>
            ) : (
              trackItems.map((item) => {
                if (item.kind === 'vessel') {
                  const entry = item.data;
                  const isSelected = selectedVessel?.vessel.id === entry.vessel.id;
                  return (
                    <div
                      key={`vessel-${entry.vessel.id}`}
                      className={'map-track-item' + (isSelected ? ' active' : '')}
                      onClick={() => {
                        selectVessel(entry, { toggleOff: isSelected });
                      }}
                    >
                      <div className="ship-icon">
                        <Ship size={18} />
                      </div>
                      <div className="info">
                        <div className="name">{entry.vessel.vesselName}</div>
                        <div className="route">
                          {entry.vessel.currentLocation.name} → {entry.vessel.nextPort || '—'}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--text-muted)',
                          background: 'transparent',
                          border: '1px solid var(--border)',
                          borderRadius: 999,
                          padding: '2px 8px',
                          flexShrink: 0,
                        }}
                      >
                        {entry.containers.length}
                      </span>
                    </div>
                  );
                }

                const entry = item.data;
                const isSelected = selectedPort?.port.id === entry.port.id;
                return (
                  <div
                    key={`port-${entry.port.id}`}
                    className={'map-track-item' + (isSelected ? ' active' : '')}
                    onClick={() => {
                      selectPort(entry, { toggleOff: isSelected });
                    }}
                  >
                    <div
                      className="ship-icon"
                      style={{
                        background: 'var(--surface)',
                        color: 'var(--text-muted)',
                        border: '1.5px solid var(--text-muted)',
                      }}
                    >
                      <Anchor size={16} />
                    </div>
                    <div className="info">
                      <div className="name">{entry.port.name}</div>
                      <div className="route">
                        {(() => {
                          const isRocnd =
                            (entry.port.id || '').toUpperCase() === 'ROCND' ||
                            (entry.port.name || '').toUpperCase().includes('CONSTAN');
                          if (isRocnd) {
                            if (isExport) {
                              return isRo
                                ? 'Port de plecare · în terminal la Constanța'
                                : 'Departure port · in terminal at Constanta';
                            }
                            return isRo
                              ? 'Port final · în terminal la Constanța'
                              : 'Final port · in terminal at Constanta';
                          }
                          return isRo
                            ? 'Containere în port / terminal'
                            : 'Containers in port / terminal';
                        })()}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        background: 'transparent',
                        border: '1px solid var(--border)',
                        borderRadius: 999,
                        padding: '2px 8px',
                        flexShrink: 0,
                      }}
                    >
                      {entry.containers.length}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="map-wrap">
        {/* Loading overlay (peste hartă) */}
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.45)',
              backdropFilter: 'blur(2px)',
              zIndex: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md, 8px)',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: 'var(--shadow-md)',
              }}
            >
              <Loader2 size={14} className="ai-spin" />
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{t('map.loading')}</span>
            </div>
          </div>
        )}

        {/* Harta Leaflet */}
        <MapContainer
          center={[35, 20]}
          zoom={4}
          style={{ height: '100%', width: '100%', background: 'var(--map-water)' }}
          zoomControl={false}
          minZoom={2}
        >
          <MapFlyTo target={mapFocus} />
          <ZoomControl position="bottomright" />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            className="map-tiles-dim"
          />

          {vesselData.map((data) => (
            <VesselMarker
              key={data.vessel.id}
              vessel={data.vessel}
              containerCount={data.containers.length}
              selected={selectedVessel?.vessel.id === data.vessel.id}
              onClick={handleVesselClick}
            />
          ))}

          {sortedPorts.map((data) => (
            <PortMarker
              key={data.port.id}
              port={data.port}
              containerCount={data.containers.length}
              onClick={() => handlePortClick(data.port.id)}
            />
          ))}
        </MapContainer>

        {/* Legendă */}
        <div className="map-legend">
          <div className="legend-item">
            <span className="dot" style={{ background: 'var(--accent)' }} />
            {t('map.legend.your_ship')}
          </div>
          <div className="legend-item">
            <span
              className="dot"
              style={{ background: 'var(--surface)', border: '1.5px solid var(--text-muted)' }}
            />
            {t('map.legend.port')}
          </div>
          <div className="legend-item">
            <span
              className="dot"
              style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)' }}
            />
            {t('map.legend.transit')}
          </div>
        </div>
        </div>
      </div>

      <VesselDetailsSidebar
        isOpen={!!selectedVessel}
        data={selectedVessel}
        onClose={() => setSelectedVessel(null)}
        onContainerClick={handleContainerClick}
      />

      <PortDetailsSidebar
        isOpen={!!selectedPort}
        data={selectedPort}
        onClose={() => setSelectedPort(null)}
        onContainerClick={handleContainerClick}
      />

      <ContainerDetailsModal
        isOpen={!!selectedContainerData}
        data={selectedContainerData}
        onClose={() => setSelectedContainerData(null)}
      />
    </div>
  );
};
